import React, { useEffect, useMemo, useRef, useState } from 'react';

interface TestimonialProps {
  name: string;
  age: string;
  treatment: string;
  content: string;
}

const Testimonial: React.FC<TestimonialProps> = ({ name, age, treatment, content }) => {
  return (
    <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100 h-full">
      <div className="flex items-center mb-4">
        <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mr-4">
          <span className="text-primary font-bold">{name.charAt(0)}</span>
        </div>
        <div>
          <h3 className="font-bold text-gray-900">{name}</h3>
          <p className="text-sm text-gray-500">{age} anos | {treatment}</p>
        </div>
      </div>
      <p className="text-gray-700 italic leading-relaxed">"{content}"</p>
    </div>
  );
};

const Testimonials: React.FC = () => {
  const testimonials = useMemo(() => ([
    {
      name: 'Mariana Costa',
      age: '35',
      treatment: 'Toxina Botulínica e Preenchimento',
      content: 'Estava insegura com as linhas de expressão que começaram a aparecer. Após o tratamento com Toxina Botulínica e preenchimento na BotoHarmony, me sinto muito mais confiante. O resultado ficou natural, exatamente o que eu queria!',
    },
    {
      name: 'Carlos M.',
      age: '42',
      treatment: 'Lipo de Papada',
      content: 'A lipo de papada transformou meu perfil. O procedimento foi tranquilo e a recuperação rápida. Estou muito satisfeito com o resultado e com o atendimento da equipe da BotoHarmony.',
    },
    {
      name: 'Juliana S.',
      age: '28',
      treatment: 'Skinbooster Avançado',
      content: 'Sofria com acne há anos e já tinha tentado diversos tratamentos sem sucesso. O protocolo de Skinbooster personalizado da BotoHarmony foi um divisor de águas. Minha pele está irreconhecível, no melhor sentido possível!',
    },
    {
      name: 'Roberto P.',
      age: '45',
      treatment: 'Bioestimuladores',
      content: 'Estava incomodado com a flacidez facial e queria algo que desse resultado sem parecer artificial. Os bioestimuladores foram a solução perfeita. O resultado é progressivo e muito natural.',
    },
    // {
    //   name: 'Ana C.',
    //   age: '38',
    //   treatment: 'Fios',
    //   content: 'Os fios deram um up no meu rosto sem precisar de cirurgia. O procedimento foi rápido e o resultado superou minhas expectativas. Recomendo muito a BotoHarmony!',
    // },
    {
      name: 'Fernanda R.',
      age: '32',
      treatment: 'Preenchimento Labial',
      content: 'Sempre tive lábios finos e queria dar um volume natural. O preenchimento labial na BotoHarmony ficou perfeito! Ninguém percebe que fiz procedimento, só elogiam que estou mais bonita.',
    },
  ]), []);

  // Carousel settings
  const getSlidesToShow = () => (window.innerWidth < 768 ? 1 : 3);
  const [slidesToShow, setSlidesToShow] = useState<number>(getSlidesToShow());
  const [current, setCurrent] = useState<number>(slidesToShow); // start at first original slide due to clones
  const [isTransitioning, setIsTransitioning] = useState<boolean>(true);
  const [isPaused, setIsPaused] = useState<boolean>(false);

  // Update slidesToShow on resize
  useEffect(() => {
    const onResize = () => {
      const next = getSlidesToShow();
      setSlidesToShow(prev => {
        if (prev !== next) {
          // Reset position to maintain continuity
          setCurrent(next);
        }
        return next;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Build extended slides to allow infinite loop
  const extendedSlides = useMemo(() => {
    const pre = testimonials.slice(-slidesToShow);
    const post = testimonials.slice(0, slidesToShow);
    return [...pre, ...testimonials, ...post];
  }, [testimonials, slidesToShow]);

  // Go to next/prev
  const next = () => {
    if (!isTransitioning) setIsTransitioning(true);
    setCurrent(c => c + 1);
  };
  const prev = () => {
    if (!isTransitioning) setIsTransitioning(true);
    setCurrent(c => c - 1);
  };

  // Handle transition end to jump seamlessly when reaching clones
  const trackRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const handle = () => {
      if (current >= testimonials.length + slidesToShow) {
        // Passed the last real slide -> jump back to first real slide
        setIsTransitioning(false);
        setCurrent(slidesToShow);
      } else if (current < slidesToShow) {
        // Passed the first clone -> jump to last real slide
        setIsTransitioning(false);
        setCurrent(testimonials.length + slidesToShow - 1);
      } else {
        setIsTransitioning(true);
      }
    };
    track.addEventListener('transitionend', handle);
    return () => track.removeEventListener('transitionend', handle);
  }, [current, testimonials.length, slidesToShow]);

  // Autoplay
  useEffect(() => {
    const id = window.setInterval(() => {
      if (!isPaused) {
        setIsTransitioning(true);
        setCurrent(c => c + 1);
      }
    }, 4000);
    return () => window.clearInterval(id);
  }, [isPaused]);

  const slideWidthPercent = 100 / slidesToShow;
  const translatePercent = current * slideWidthPercent;

  return (
    <section className="py-16 bg-light">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h2 className="section-title">Depoimentos de Clientes</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Confira os relatos de clientes que transformaram sua aparência e autoestima com nossos tratamentos estéticos.
          </p>
        </div>

        {/* Carousel */}
        <div
          className="relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Viewport */}
          <div className="overflow-hidden rounded-2xl">
            {/* Track */}
            <div
              ref={trackRef}
              className={`flex ${isTransitioning ? 'transition-transform duration-500 ease-out' : ''}`}
              style={{ transform: `translateX(-${translatePercent}%)` }}
              aria-roledescription="carousel"
              aria-label="Depoimentos"
            >
              {extendedSlides.map((t, index) => (
                <div
                  key={index}
                  className="shrink-0 px-3 md:px-4"
                  style={{ flex: `0 0 ${slideWidthPercent}%` }}
                  aria-roledescription="slide"
                >
                  <Testimonial {...t} />
                </div>
              ))}
            </div>
          </div>

          {/* Controls */}
          <button
            onClick={prev}
            className="absolute -left-4 sm:-left-5 md:-left-8 lg:-left-10 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/90 hover:bg-white shadow-lg hover:shadow-xl flex items-center justify-center transition-all duration-300 group border border-gray-200 hover:border-secondary z-20"
            aria-label="Anterior"
          >
            <svg className="w-5 h-5 md:w-6 md:h-6 text-gray-600 group-hover:text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={next}
            className="absolute -right-4 sm:-right-5 md:-right-8 lg:-right-10 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/90 hover:bg-white shadow-lg hover:shadow-xl flex items-center justify-center transition-all duration-300 group border border-gray-200 hover:border-secondary z-20"
            aria-label="Próximo"
          >
            <svg className="w-5 h-5 md:w-6 md:h-6 text-gray-600 group-hover:text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;